import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./AcquireRawMaterialPanel.module.css";
import WebService from "../Services/WebService";
import AsyncSelect from "react-select/async";
import { FaTimes, FaPlus, FaTrash, FaPen } from "react-icons/fa";
import { toast } from "react-toastify";

interface Props {
  saleOrderId: number;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface DropdownOption {
  label: string;
  value: number;
  measuring_unit?: string;
}

export interface MaterialEntry {
  raw_material_id: number;
  name: string;
  quantity: number;
  measuring_unit: string;
  acquisition_id?: number;
}

interface AcquiredMaterial {
  acquisition_id: number;
  raw_material_id: number;
  raw_material_name: string;
  quantity: number;
  measuring_unit: string;
}

const PAGE_SIZE = 20;
const AcquireRawMaterialPanel: React.FC<Props> = ({
  saleOrderId,
  open,
  onClose,
  onSuccess,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<DropdownOption | null>(null);
  const [quantity, setQuantity] = useState<number>(0);
  const [unit, setUnit] = useState<string>("");

  // For editing an entry from summary table
  const [editState, setEditState] = useState<AcquiredMaterial | null>(null);

  // Summary of acquired materials for this sale order
  const [summary, setSummary] = useState<AcquiredMaterial[]>([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [autoAcquireLoading, setAutoAcquireLoading] = useState(false);
  const [autoAcquired, setAutoAcquired] = useState<AcquiredMaterial[]>([]);

  // ---------- SUMMARY (unchanged) ----------
  const fetchSummary = useCallback(
    async (options?: { setAutoList?: boolean }): Promise<AcquiredMaterial[]> => {
      setLoadingSummary(true);
      try {
        const res: any = await WebService.getAPI<any>({
          action: `/v1/door-management/saleorders/acquire-raw-materials/${saleOrderId}`,
        });

        const list: AcquiredMaterial[] =
          res?.status_code === 200 && Array.isArray(res?.data) ? res.data : [];

        setSummary(list);
        if (options?.setAutoList) setAutoAcquired(list);
        return list;
      } catch {
        setSummary([]);
        if (options?.setAutoList) setAutoAcquired([]);
        return [];
      } finally {
        setLoadingSummary(false);
      }
    },
    [saleOrderId]
  );

  useEffect(() => {
    if (!open) return;
    fetchSummary();
    // reset form
    setEditState(null);
    setSelectedCategory(null);
    setSelectedMaterial(null);
    setUnit("");
    setQuantity(0);
    setAutoAcquired([]);
  }, [open, saleOrderId, fetchSummary]);

  // ---------- BACKEND SEARCH LOADERS ----------
  const loadCategoryOptions = useCallback(
    async (inputValue: string): Promise<DropdownOption[]> => {
      // /raw-material/categories?type=NO_SUB_CATEGORY&like=<text>
      const params = new URLSearchParams({
        type: "NO_SUB_CATEGORY",
        page_number: "1",
        limit: String(PAGE_SIZE),
      });
      const q = (inputValue || "").trim();
      if (q.length) params.append("like", q);

      try {
        const res: any = await WebService.getAPI({
          action: `/v1/door-management/raw-material/categories?${params.toString()}`,
        });

        // Accept res.data OR res.data.data
        const raw = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
          ? res.data.data
          : [];

        return raw.map((cat: any) => ({
          label: cat?.category_name ?? `#${cat?.category_id}`,
          value: Number(cat?.category_id),
        }));
      } catch {
        return [];
      }
    },
    []
  );

  const loadMaterialOptions = useCallback(
    async (inputValue: string): Promise<DropdownOption[]> => {
    
      const params = new URLSearchParams({
        page_number: "1",
        limit: String(PAGE_SIZE),
        status: "ACTIVE",
        sort_order: "asc",
      });
      if (selectedCategory) params.append("category_id", String(selectedCategory));
      const q = (inputValue || "").trim();
      if (q.length) params.append("rm_like", q);

      try {
        const res: any = await WebService.getAPI({
          action: `/v1/door-management/raw-material?${params.toString()}`,
        });

        
        const list =
          (res?.data?.raw_materials && Array.isArray(res.data.raw_materials)
            ? res.data.raw_materials
            : res?.data?.data?.raw_materials && Array.isArray(res.data.data.raw_materials)
            ? res.data.data.raw_materials
            : Array.isArray(res?.data)
            ? res.data
            : []) || [];

        return list.map((mat: any) => ({
          label: mat?.name ?? `#${mat?.id}`,
          value: Number(mat?.id),
          measuring_unit: mat?.measuring_unit ?? "",
        }));
      } catch {
        return [];
      }
    },
    [selectedCategory]
  );

  // ---------- HANDLERS ----------
  const handleMaterialChange = (opt: DropdownOption | null) => {
    setSelectedMaterial(opt);
    setUnit(opt?.measuring_unit || "");
  };

  const handleAdd = async () => {
    if (!selectedMaterial || quantity <= 0) {
      toast.error("Please fill all fields properly.");
      return;
    }
    const payload = {
      sale_order_id: saleOrderId,
      requests: [
        {
          raw_material_id: selectedMaterial.value,
          quantity,
        },
      ],
    };

    try {
      const res: any = await WebService.postAPI({
        action: `/v1/door-management/saleorders/acquire-raw-materials`,
        body: payload,
      });
      if (res.status_code === 200) {
        toast.success("Raw material added.");
        fetchSummary();
        setSelectedMaterial(null);
        setQuantity(0);
        setUnit("");
        setSelectedCategory(null);
        onSuccess?.();
      } else {
        toast.error("Failed to add material.");
      }
    } catch {
      toast.error("Failed to add material.");
    }
  };

  const handleEditSave = async () => {
    if (!selectedMaterial || quantity <= 0 || !editState) {
      toast.error("Please fill all fields properly.");
      return;
    }
    const payload = {
      sale_order_id: saleOrderId,
      requests: [
        {
          acquisition_id: editState.acquisition_id,
          raw_material_id: selectedMaterial.value,
          quantity,
        },
      ],
    };

    try {
      const res: any = await WebService.postAPI({
        action: `/v1/door-management/saleorders/acquire-raw-materials`,
        body: payload,
      });
      if (res.status_code === 200) {
        toast.success("Raw material updated.");
        fetchSummary();
        setEditState(null);
        setSelectedMaterial(null);
        setQuantity(0);
        setUnit("");
        setSelectedCategory(null);
        onSuccess?.();
      } else {
        toast.error("Failed to update material.");
      }
    } catch {
      toast.error("Failed to update material.");
    }
  };

  const handleDelete = async (acquisition_id: number) => {
    try {
      await WebService.deleteAPI({
        action: `/v1/door-management/saleorders/acquire-raw-materials/${acquisition_id}`,
      });
      toast.success("Raw material deleted.");
      fetchSummary();
      onSuccess?.();
    } catch {
      // toast.error("Failed to delete raw material.");
    }
  };

  const handleEditFromSummary = (mat: AcquiredMaterial) => {
    setEditState(mat);
    setSelectedMaterial({
      label: mat.raw_material_name,
      value: mat.raw_material_id,
      measuring_unit: mat.measuring_unit,
    });
    setUnit(mat.measuring_unit);
    setQuantity(mat.quantity);
    
  };

  const handleCancelEdit = () => {
    setEditState(null);
    setSelectedCategory(null);
    setSelectedMaterial(null);
    setUnit("");
    setQuantity(0);
  };

  const handleAutoAcquire = async () => {
    setAutoAcquireLoading(true);
    try {
      const res: any = await WebService.postAPI({
        action: `/v1/door-management/saleorders/${saleOrderId}/acquire-raw-materials/auto`,
        body: {},
      });
      if (res.status_code === 200) {
        const created = Array.isArray(res.data) ? res.data : [];
        if (created.length === 0) {
          toast.info("No product raw materials were auto-acquired. Add them manually if needed.");
        } else {
          toast.success("Raw materials acquired automatically.");
        }
        await fetchSummary({ setAutoList: true });
        onSuccess?.();
      } else {
        toast.error("Failed to auto acquire materials.");
      }
    } catch {
      toast.error("Failed to auto acquire materials.");
    } finally {
      setAutoAcquireLoading(false);
    }
  };

  const noOptionsMsg = useMemo(
    () => ({ inputValue }: { inputValue: string }) =>
      inputValue?.trim().length ? "No results" : "Type to search…",
    []
  );

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.panel}>
        <div className={styles.header}>
          <h3>Acquire Raw Material</h3>
          <FaTimes className={styles.closeIcon} onClick={onClose} />
        </div>
        <div className={styles.sectionDivider} />
        {/* --- Auto Acquire Section --- */}
        <div className={styles.autoAcquireSection}>
          <span className={styles.autoAcquireText}>Automatic Acquire Raw Material</span>
          <button
            className={styles.autoAcquireBtn}
            onClick={handleAutoAcquire}
            disabled={autoAcquireLoading}
            type="button"
          >
            {autoAcquireLoading ? "Processing..." : "Submit"}
          </button>
        </div>

        {/* --- Auto acquired list (this run) --- */}
        {autoAcquired.length > 0 && (
          <div className={styles.autoList}>
            <h4 className={styles.autoListTitle}>Auto-acquired</h4>
            <table className={styles.autoListTable}>
              <thead>
                <tr>
                  <th>Sr No</th>
                  <th>Material</th>
                  <th>Qty</th>
                  <th>Unit</th>
                </tr>
              </thead>
              <tbody>
                {autoAcquired.map((m, idx) => (
                  <tr key={`${m.acquisition_id}-${idx}`}>
                    <td style={{ width: 60 }}>{idx + 1}</td>
                    <td>{m.raw_material_name}</td>
                    <td>{m.quantity}</td>
                    <td>{m.measuring_unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* --- Search + Select (server-side) --- */}
        <div className={styles.formSection}>
          <div className={styles.row}>
            {/* Categories: type=NO_SUB_CATEGORY & like=... */}
            <AsyncSelect
              cacheOptions
              defaultOptions
              loadOptions={loadCategoryOptions}
              onChange={(opt) => {
                setSelectedCategory((opt as any)?.value ?? null);
                // reset material whenever category changes
                setSelectedMaterial(null);
                setUnit("");
              }}
              placeholder="Select Category"
              className={styles.selectInput}
              isClearable
              isDisabled={!!editState}
              noOptionsMessage={noOptionsMsg}
            />

            {/* Materials: rm_like, optionally filtered by category_id */}
            <AsyncSelect
              key={selectedCategory ?? "no-cat"} 
              cacheOptions
              defaultOptions
              loadOptions={loadMaterialOptions}
              value={selectedMaterial}
              onChange={(opt) => handleMaterialChange(opt as DropdownOption)}
              placeholder="Select Material"
              className={styles.selectInput}
              isSearchable
              isClearable
              isDisabled={!!editState}
              noOptionsMessage={noOptionsMsg}
            />
          </div>

          <div className={styles.row}>
            <input
              type="number"
              value={quantity}
              min={1}
              onChange={(e) => setQuantity(Number(e.target.value))}
              placeholder="Quantity"
              className={styles.textInput}
            />
            <input
              type="text"
              value={unit}
              placeholder="Unit"
              className={styles.textInput}
              readOnly
            />

            {editState ? (
              <>
                <button className={styles.addBtn} onClick={handleEditSave} type="button">
                  <FaPen /> Update
                </button>
                <button
                  className={styles.addBtn}
                  onClick={handleCancelEdit}
                  type="button"
                  style={{ background: "#eee", color: "#666" }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                className={styles.addBtn}
                onClick={handleAdd}
                type="button"
                disabled={!selectedMaterial || quantity <= 0}
              >
                <FaPlus /> Add
              </button>
            )}
          </div>
        </div>

        {/* --- Summary Table --- */}
        <div className={styles.materialList}>
          <h4 className={styles.summaryTitle}>Acquired Raw Materials</h4>
          {loadingSummary ? (
            <div style={{ padding: 16 }}>Loading...</div>
          ) : summary.length === 0 ? (
            <div style={{ color: "#888", padding: 12 }}>
              No raw materials acquired for this sale order.
            </div>
          ) : (
            <table className={styles.summaryTable}>
              <thead>
                <tr>
                  <th>Material</th>
                  <th>Quantity</th>
                  <th>Unit</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((mat) => (
                  <tr key={mat.acquisition_id}>
                    <td>{mat.raw_material_name}</td>
                    <td>{mat.quantity}</td>
                    <td>{mat.measuring_unit}</td>
                    <td>
                      <FaPen
                        title="Edit"
                        style={{ cursor: "pointer", color: "#006be6", marginRight: 18 }}
                        onClick={() => handleEditFromSummary(mat)}
                      />
                      <FaTrash
                        title="Delete"
                        style={{ cursor: "pointer", color: "#e43e2d" }}
                        onClick={() => handleDelete(mat.acquisition_id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.submitBtn} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcquireRawMaterialPanel;
